import React, { createContext, useContext, useMemo } from 'react';
import { logError } from '../../../utils/logger/logError';
import { type BigQueryEvents, type Variant, createEvents } from './events';

const BigQueryContext = createContext<BigQueryEvents | null>(null);

export const useBigQuery = (): BigQueryEvents => {
  const bigQuery = useContext(BigQueryContext);
  if (!bigQuery) {
    throw new TypeError('useBigQuery must be used inside "BigQuery" root component');
  }
  return bigQuery;
};

export interface BigQueryPublicProps {
  /**
   * Defines if BigQuery is enabled or not
   */
  bigQueryEnabled?: boolean;
  /**
   * The options for BigQuery (must be provided when [bigQueryEnabled=true])
   */
  bigQueryOptions?: {
    /**
     * Consumer page name in "my_page" format
     */
    pageName: string;
    /**
     * Defines the position of the component in the page.
     * Optional. Relevant only for "carousel" variant and when there are multiple carousels in the page.
     * The count starts at 1.
     */
    containerPositionInPage?: number;
  };
}

type BigQueryProps = BigQueryPublicProps & {
  children: React.ReactNode;
  variant: Variant;
};

export const BigQuery = ({ children, variant, bigQueryEnabled = false, bigQueryOptions }: BigQueryProps) => {
  const enabled = bigQueryEnabled;
  const pageName = bigQueryOptions?.pageName || '';
  const containerPositionInPage = bigQueryOptions?.containerPositionInPage || 1;

  if (enabled && !pageName) {
    logError('BigQuery', 'must provide "bigQueryOptions.pageName" when [bigQueryEnabled=true]');
  }

  const events = useMemo<BigQueryEvents>(
    () =>
      createEvents({
        enabled,
        variant,
        pageName,
        containerPositionInPage,
      }),
    [enabled, variant, pageName, containerPositionInPage]
  );
  return <BigQueryContext.Provider value={events}>{children}</BigQueryContext.Provider>;
};
